import type { User } from "firebase/auth";
import { loadCharacterProfile } from "@/character/storage";
import {
  createCreatorScene,
  deleteCreatorScene,
  listCreatorScenes,
  type CreatorSceneMeta,
} from "@/home/scene-room-storage";
import { creatorSceneEditorPath } from "@/pages/creator-routes";

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function formatUpdated(at: number): string {
  try {
    return new Date(at).toLocaleString("th-TH", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

export async function mountCreatorPage(
  root: HTMLElement,
  user: User,
): Promise<() => void> {
  const profile = loadCharacterProfile(user.uid);
  if (!profile) {
    window.location.replace("/character/create");
    return () => {};
  }

  root.className = "page-shell page-creator";

  const renderList = (scenes: CreatorSceneMeta[], hint = "") => {
    const rows =
      scenes.length === 0
        ? `<p class="hint creator-empty">ยังไม่มีฉาก · สร้างฉากแรกด้านล่าง</p>`
        : `<ul class="creator-scene-list">
            ${scenes
              .map(
                (scene) => `
              <li class="creator-scene-row">
                <a class="creator-scene-link" href="${creatorSceneEditorPath(scene.id)}">
                  <span class="creator-scene-name">${escapeHtml(scene.name)}</span>
                  <span class="creator-scene-meta hint">แก้ไขล่าสุด ${formatUpdated(scene.updatedAt)}</span>
                </a>
                <button type="button" class="btn btn-ghost btn-xs creator-scene-delete" data-scene-id="${escapeHtml(scene.id)}" aria-label="ลบฉาก">
                  ลบ
                </button>
              </li>`,
              )
              .join("")}
          </ul>`;

    root.innerHTML = `
      <main class="auth-card creator-card" aria-labelledby="creator-title">
        <p class="eyebrow">Scene creator</p>
        <h1 id="creator-title">สร้างฉาก</h1>
        <p class="lede">
          ใช้เครื่องมือเดียวกับหน้าบ้าน — อัปโหลดชิ้น วางกริด ตั้งฉากหลัง · แยกฉากได้หลายไฟล์
        </p>
        ${hint ? `<p class="hint creator-hint" role="status">${escapeHtml(hint)}</p>` : ""}
        ${rows}
        <form id="creator-new-form" class="creator-new-form">
          <label class="field">
            <span class="field-label">ชื่อฉากใหม่</span>
            <input type="text" id="creator-new-name" class="text-input" maxlength="48" placeholder="เช่น ป่า · ห้องบอส" required />
          </label>
          <button type="submit" class="btn btn-primary">สร้างและแก้ไข</button>
        </form>
        <div class="creator-foot-links">
          <a class="btn btn-ghost" href="/home">← บ้านของฉัน</a>
          <a class="text-link" href="/home?character=1">ตัวละคร</a>
        </div>
      </main>
    `;
  };

  let scenes = await listCreatorScenes(user.uid);
  renderList(scenes);

  const onSubmit = (event: Event) => {
    event.preventDefault();
    const input = root.querySelector<HTMLInputElement>("#creator-new-name");
    const name = input?.value ?? "";
    void (async () => {
      try {
        const meta = await createCreatorScene(user.uid, name);
        window.location.assign(creatorSceneEditorPath(meta.id));
      } catch (err) {
        renderList(scenes, err instanceof Error ? err.message : "สร้างฉากไม่สำเร็จ");
      }
    })();
  };

  const onClick = (event: Event) => {
    const btn = (event.target as HTMLElement).closest<HTMLButtonElement>(".creator-scene-delete");
    if (!btn) return;
    const sceneId = btn.dataset.sceneId;
    if (!sceneId) return;
    const scene = scenes.find((s) => s.id === sceneId);
    if (!scene) return;
    if (!window.confirm(`ลบฉาก "${scene.name}"?\n\nข้อมูลชิ้นและการวางในฉากนี้จะหาย`)) {
      return;
    }
    void (async () => {
      try {
        await deleteCreatorScene(user.uid, sceneId);
        scenes = await listCreatorScenes(user.uid);
        renderList(scenes, `ลบ "${scene.name}" แล้ว`);
      } catch (err) {
        renderList(scenes, err instanceof Error ? err.message : "ลบไม่สำเร็จ");
      }
    })();
  };

  root.addEventListener("submit", onSubmit);
  root.addEventListener("click", onClick);

  return () => {
    root.removeEventListener("submit", onSubmit);
    root.removeEventListener("click", onClick);
  };
}
